/**
 * OptimizedCanvasBoard.tsx
 * 
 * 职责：
 * 1. 优化CanvasBoard组件，解决长时间拖拽元素后画布卡顿的问题
 * 2. 使用renderOptimizer提供的优化机制
 * 3. 区分画布拖拽和元素拖拽，针对性地优化渲染
 */

import React, { useEffect } from 'react';
import CanvasBoard from './CanvasBoard';
import { useRenderOptimizer } from '../../utils/renderOptimizer';
import { CanvasMode } from '../../types';

// 继承CanvasBoard的所有属性
type OptimizedCanvasBoardProps = React.ComponentProps<typeof CanvasBoard>;

const OptimizedCanvasBoard: React.FC<OptimizedCanvasBoardProps> = (props) => {
  // 获取SelectionService和ViewportService
  const { selectionService, viewportService } = props;
  
  // 使用渲染优化器，传入一个空函数，实际渲染会在CanvasBoard中进行
  const renderOptimizer = useRenderOptimizer(() => {}, 33);
  
  // 监听元素拖拽和视口拖拽
  useEffect(() => {
    // 监听元素拖拽开始
    const handleSelectionMoveStart = () => {
      renderOptimizer.setOperationType('element_drag');
      renderOptimizer.startRenderLoop();
    };
    
    // 监听元素拖拽结束
    const handleSelectionMoveEnd = () => {
      renderOptimizer.resetRenderState();
      renderOptimizer.stopRenderLoop();
    };
    
    // 监听视口拖拽开始
    const handleViewportDragStart = () => {
      renderOptimizer.setOperationType('viewport_drag');
      renderOptimizer.startRenderLoop();
    };
    
    // 监听视口拖拽结束
    const handleViewportDragEnd = () => {
      renderOptimizer.resetRenderState();
      renderOptimizer.stopRenderLoop();
    };
    
    // 监听元素移动事件
    const handleElementMove = () => {
      renderOptimizer.incrementOperationCount();
    };
    
    // 添加自定义事件监听器
    document.addEventListener('selection-move-start', handleSelectionMoveStart);
    document.addEventListener('selection-move-end', handleSelectionMoveEnd);
    document.addEventListener('viewport-drag-start', handleViewportDragStart);
    document.addEventListener('viewport-drag-end', handleViewportDragEnd);
    document.addEventListener('element-move', handleElementMove);
    
    // 清理函数
    return () => {
      document.removeEventListener('selection-move-start', handleSelectionMoveStart);
      document.removeEventListener('selection-move-end', handleSelectionMoveEnd);
      document.removeEventListener('viewport-drag-start', handleViewportDragStart);
      document.removeEventListener('viewport-drag-end', handleViewportDragEnd);
      document.removeEventListener('element-move', handleElementMove);
    };
  }, [renderOptimizer]);
  
  // 监听SelectionService和ViewportService的状态变化
  useEffect(() => {
    if (!selectionService || !viewportService) return;
    
    // 创建代理方法，在原始方法执行前后触发事件
    const originalStartMoving = selectionService.startMoving;
    const originalStopMoving = selectionService.stopMoving;
    const originalMoveSelectedContent = selectionService.moveSelectedContent;
    
    const originalStartDrag = viewportService.startDrag;
    const originalStopDrag = viewportService.stopDrag;
    
    // 重写SelectionService的方法
    selectionService.startMoving = function(point) {
      document.dispatchEvent(new CustomEvent('selection-move-start'));
      return originalStartMoving.call(this, point);
    };
    
    selectionService.stopMoving = function() {
      document.dispatchEvent(new CustomEvent('selection-move-end'));
      return originalStopMoving.call(this);
    };
    
    selectionService.moveSelectedContent = function(point) {
      document.dispatchEvent(new CustomEvent('element-move'));
      return originalMoveSelectedContent.call(this, point);
    };
    
    // 重写ViewportService的方法
    viewportService.startDrag = function(x, y) {
      document.dispatchEvent(new CustomEvent('viewport-drag-start'));
      return originalStartDrag.call(this, x, y);
    };
    
    viewportService.stopDrag = function() {
      document.dispatchEvent(new CustomEvent('viewport-drag-end'));
      return originalStopDrag.call(this);
    };
    
    // 清理函数，恢复原始方法
    return () => {
      selectionService.startMoving = originalStartMoving;
      selectionService.stopMoving = originalStopMoving;
      selectionService.moveSelectedContent = originalMoveSelectedContent;
      
      viewportService.startDrag = originalStartDrag;
      viewportService.stopDrag = originalStopDrag;
    };
  }, [selectionService, viewportService]);
  
  // 性能监测功能已移除
  
  // 渲染原始CanvasBoard组件，传递所有属性
  return <CanvasBoard {...props} />;
};

export default OptimizedCanvasBoard;